import { graphHooks } from '@tunasong/graph-lib/react'
import { profilesApi } from '@tunasong/redux'
import type { ProfileGear } from '@tunasong/schemas'
import { normalizeParts } from '@tunasong/schemas'
import { Button, DebouncedTextField, Typography, useUserConfigSave } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { ChoiceGroup } from './choice-group.js'
import { GearRows } from './gear-rows.js'
import { InputRows } from './input-rows.js'
import { PresetStep } from './preset-step.js'
import type { RowRemoval } from './row-types.js'
import { inputRowKey, monitorOptions } from './row-types.js'
import { NameStep } from './name-step.js'
import { PartsStep } from './parts-step.js'
import { bandParts } from './parts.js'
import { SaveStatusLine } from './save-status.js'
import { addInputs, withUpdate } from './setup-edits.js'
import type { NewStageInput } from './setup-edits.js'
import { sourceSuggestions } from './suggestions.js'
import { UndoSnackbar } from './undo-snackbar.js'
import { useFocusRequest } from './use-focus-request.js'
import { useSaveTracker } from './use-save-tracker.js'
import styles from './stage-setup-editor.module.css'

export interface StageSetupEditorProps {
  /** In the band dialog: "What do you play?" edits this band's parts. In Profile: the default parts. */
  band?: { id: string; name: string }
}

/**
 * The member's stage setup (docs/ux/2026-10-16-rider-collaboration-ux.md, Phase 1), in the order a musician thinks:
 * their name if the venue would not otherwise have one, what they play, inputs, gear, monitoring, a note for the
 * engineer. Every change saves on its own; the status line says so only once the write is confirmed.
 */
export const StageSetupEditor: FC<StageSetupEditorProps> = props => {
  const { band } = props
  const [gear, saveGear, hasLoaded] = useUserConfigSave('gear')
  const [performance, savePerformance] = useUserConfigSave('performance')
  const { profile } = graphHooks.useProfile()
  const [updateProfile] = profilesApi.useUpdateProfileMutation()
  const tracker = useSaveTracker()
  const focus = useFocusRequest()
  const [removal, setRemoval] = useState<RowRemoval | null>(null)
  /** The "Add inputs" prompt opened the preset step for a member who already lists gear */
  const [presetsOpened, setPresetsOpened] = useState(false)

  /** Decided once per session, so the name field never vanishes while someone types the first name */
  const [showName, setShowName] = useState<boolean | undefined>(undefined)
  if (profile && showName === undefined) {
    setShowName(!profile.name?.trim())
  }

  /** The latest setup, also between a save and the re-render it causes: debounced fields edit what is current */
  const latestGear = useRef<ProfileGear | undefined>(gear)
  useEffect(() => {
    latestGear.current = gear
  }, [gear])

  const update = (edit: (current: ProfileGear | undefined) => ProfileGear) => {
    const next = edit(latestGear.current)
    latestGear.current = next
    void tracker.track(() => saveGear(next))
  }

  if (!hasLoaded) {
    return null
  }

  const inputs = gear?.inputs ?? []
  const requirements = gear?.requirements ?? []
  const parts = band ? bandParts(performance, band.id) : (normalizeParts(performance?.defaultParts) ?? [])

  const handleName = (name: string) => {
    if (!profile) {
      return
    }
    void tracker.track(() => updateProfile({ id: profile.id, profile: { name } }).unwrap())
  }

  /** Presets are offered while there are no inputs: at once for an empty setup, on request when there is gear */
  const showPresets = inputs.length === 0 && (requirements.length === 0 || presetsOpened)

  const handleAddPresets = (rows: NewStageInput[]) => {
    let firstId: string | undefined
    update(current => {
      const next = addInputs(current, rows)
      firstId = next.inputs?.[(current?.inputs ?? []).length]?.id
      return next
    })
    setPresetsOpened(false)
    if (firstId) {
      focus.request(inputRowKey(firstId))
    }
  }

  return (
    <div className={styles.root}>
      <Typography variant="body2" color="textSecondary" className={styles.description}>
        What you need on stage. Bandmates use this to build riders. It&apos;s visible to other Tunasong users.
      </Typography>
      <SaveStatusLine status={tracker.status} updatedAt={gear?.updatedAt} onRetry={() => void tracker.retry()} />

      {showName ? <NameStep name={profile?.name} onSave={handleName} /> : null}

      <PartsStep
        band={band}
        performance={performance}
        otherInstruments={gear?.otherInstruments}
        onPerformanceChange={next => void tracker.track(() => savePerformance(next))}
        onOtherChange={otherInstruments => update(current => withUpdate(current, { otherInstruments }))}
      />

      {inputs.length === 0 && requirements.length > 0 && !presetsOpened ? (
        <div className={styles.notice}>
          <Typography variant="body2">Add your inputs so the rider can number your channels.</Typography>
          <Button variant="outlined" size="small" onClick={() => setPresetsOpened(true)}>
            Add inputs
          </Button>
        </div>
      ) : null}

      {showPresets ? <PresetStep parts={parts} autoFocus={presetsOpened} onAdd={handleAddPresets} /> : null}

      <InputRows
        inputs={inputs}
        sourceSuggestions={sourceSuggestions(parts)}
        update={update}
        onRemoved={setRemoval}
        focus={focus}
      />
      <GearRows requirements={requirements} update={update} onRemoved={setRemoval} focus={focus} />

      <section className={styles.section}>
        <ChoiceGroup
          legend="Monitoring"
          options={monitorOptions}
          value={gear?.monitorPreference}
          onChange={monitorPreference => update(current => withUpdate(current, { monitorPreference }))}
        />
      </section>

      <section className={styles.section}>
        <DebouncedTextField
          className={styles.field}
          label="Notes for the sound engineer"
          multiline
          rows={3}
          value={gear?.notes ?? ''}
          onDebouncedChange={notes => update(current => withUpdate(current, { notes: notes.trim() || undefined }))}
        />
      </section>

      {removal ? (
        <UndoSnackbar
          message={removal.message}
          onUndo={() => {
            removal.onUndo()
            setRemoval(null)
          }}
          onClose={() => setRemoval(null)}
        />
      ) : null}
    </div>
  )
}

export default StageSetupEditor
